import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MessageSquare, Send, X, Bot } from 'lucide-react';


export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [showBot, setShowBot] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    { text: "Hi, I'm Nabula! Ask me anything about Dharshith's experience, his web development projects, or his tech stack.", sender: 'bot' }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Auto-scroll to the bottom when new messages arrive
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

useEffect(() => {
  const handleScroll = () => {
    setShowBot(true);
    window.removeEventListener('scroll', handleScroll);
  };

  window.addEventListener('scroll', handleScroll);

  return () => {
    window.removeEventListener('scroll', handleScroll);
  };
}, []);

const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userText = input;
    setInput('');
    setMessages((prev) => [...prev, { text: userText, sender: 'user' }]);
    setIsLoading(true);

    const isDev = process.env.REACT_APP_REACT_API_RUNNING === 'development';
    const backendUrl = isDev 
      ? 'http://localhost:5000/api/chat' 
      : '/.netlify/functions/chat';

    try { 
      const response = await axios.post(backendUrl, { message: userText });
      const reply = response.data.reply;
      setMessages((prev) => [...prev, { text: reply, sender: 'bot' }]);
    } catch (error) {
      console.error("Chat error:", error);
      
      // Your existing fallback system...
      let fallbackText = "I'm experiencing heavy traffic.";
      setMessages((prev) => [...prev, { text: fallbackText, sender: 'bot' }]);
    } finally {
      setIsLoading(false);
    }
  };

  // --- INLINE STYLES (Guaranteed to work anywhere) ---
  const styles = {
    container: {
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      zIndex: 99999,
      fontFamily: 'system-ui, -apple-system, sans-serif'
    },
    triggerButton: {
      width: '56px',
      height: '56px',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      borderRadius: '50%',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3)',
      transition: 'transform 0.2s ease',
    },
    chatWindow: {
      width: '350px',
      height: '450px',
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.25), 0 10px 10px -5px rgba(0, 0, 0, 0.15)',
      border: '1px solid #e5e7eb',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    header: {
      backgroundColor: '#2563eb',
      padding: '14px 16px',
      color: '#ffffff',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    headerInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    headerTitle: {
      fontWeight: '600',
      fontSize: '14px',
      margin: 0,
      lineHeight: 1,
    },
    headerSubtitle: {
      fontSize: '11px',
      color: '#93c5fd',
    },
    closeButton: {
      background: 'none',
      border: 'none',
      color: '#ffffff',
      cursor: 'pointer',
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
    },
    messagesContainer: {
      flex: 1,
      padding: '16px',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      backgroundColor: '#f9fafb',
    },
    messageRow: (sender) => ({
      display: 'flex',
      gap: '8px',
      alignItems: 'flex-end',
      justifyContent: sender === 'user' ? 'flex-end' : 'flex-start',
    }),
    botIconWrapper: {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: '#dbeafe',
      color: '#2563eb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    bubble: (sender) => ({
      maxWidth: '75%',
      padding: '10px 14px',
      borderRadius: '16px',
      fontSize: '13px',
      lineHeight: '1.4',
      wordBreak: 'break-word',
      backgroundColor: sender === 'user' ? '#2563eb' : '#ffffff',
      color: sender === 'user' ? '#ffffff' : '#1f2937',
      border: sender === 'user' ? 'none' : '1px solid #e5e7eb',
      boxShadow: sender === 'user' ? 'none' : '0 1px 2px rgba(0,0,0,0.05)',
      borderRadiusStyle: sender === 'user' ? '16px 16px 0px 16px' : '16px 16px 16px 0px',
    }),
    inputFooter: {
      padding: '12px',
      borderTop: '1px solid #f3f4f6',
      backgroundColor: '#ffffff',
      display: 'flex',
      gap: '8px',
    },
    inputField: {
      flex: 1,
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      padding: '10px 12px',
      fontSize: '13px',
      outline: 'none',
      color: '#000000',
      backgroundColor: '#ffffff',
    },
    sendButton: {
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '12px',
      padding: '8px 12px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    typingDots: {
      display: 'flex',
      gap: '4px',
      alignItems: 'center',
      padding: '4px 0',
    }
  };

  return (
    <div style={styles.container}>
      
      {/* 1. Chat Trigger Button */}
      {!isOpen && showBot &&(
        <button
            onClick={() => {
            const audio = new Audio('/sounds/pop.mp3');
            audio.volume = 0.3;
            audio.play().catch(() => {});
            setIsOpen(true);
          }}
          style={styles.triggerButton}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <MessageSquare size={24} />
        </button>
      )}

      {/* 2. Active Chat Box */}
      {isOpen && (
        <div style={styles.chatWindow}>
          
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerInfo}>
              <div style={{ ...styles.botIconWrapper, backgroundColor: '#ffffff', color: '#2563eb' }}>
                <Bot size={14} />
              </div>
              <div>
                <h3 style={styles.headerTitle}>Nabula</h3>
                <span style={styles.headerSubtitle}>Dharshith's Assistant</span>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} style={styles.closeButton}>
              <X size={20} />
            </button>
          </div>

          {/* Messages Log */}
          <div style={styles.messagesContainer}>
            {messages.map((msg, index) => (
              <div key={index} style={styles.messageRow(msg.sender)}>
                {msg.sender === 'bot' && (
                  <div style={styles.botIconWrapper}>
                    <Bot size={12} />
                  </div>
                )}
                <div 
                  style={{
                    ...styles.bubble(msg.sender),
                    borderRadius: msg.sender === 'user' ? '16px 16px 0px 16px' : '16px 16px 16px 0px'
                  }}
                >
                  {msg.text}
                </div>
              </div>
            ))}

            {/* Loading bubble */}
            {isLoading && (
              <div style={styles.messageRow('bot')}>
                <div style={styles.botIconWrapper}>
                  <Bot size={12} />
                </div>
                <div style={{ ...styles.bubble('bot'), borderRadius: '16px 16px 16px 0px' }}>
                  <div style={styles.typingDots}>
                    <span style={{ color: '#9ca3af' }}>Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Form Input Footer */}
          <form onSubmit={handleSend} style={styles.inputFooter}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me something about Dharshith..."
              style={styles.inputField}
            />
            <button
              type="submit"
              disabled={isLoading || !input.trim()}
              style={{
                ...styles.sendButton,
                opacity: (isLoading || !input.trim()) ? 0.5 : 1,
                cursor: (isLoading || !input.trim()) ? 'default' : 'pointer'
              }}
            >
              <Send size={16} />
            </button>
          </form>

        </div>
      )}
    </div>
  );
}